import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs'

import { logAndAddBreadcrumb } from '../../utils/errorTools'
import { accessTokenExpiresAt, expiringSoonThreshold } from '../auth/expiration'
import {
  AccessTokenFactoryOptions,
  AuthState,
  authSubject,
  latestAccessToken$,
  latestAccessTokenFactory$,
  latestAuthState$,
  latestLocalLogout$,
  signalRemoteSignOut,
} from '../auth/state'
import {
  byTagAndSource,
  CrossTabChannel,
  crossTabChannel,
  CrossTabMessage,
  latestVisibleByTag,
  VisibilityGate,
} from './channel'

export type AuthMessage = Extract<CrossTabMessage, { readonly _tag: 'auth' }>

export type AuthPair = Pick<AuthMessage, 'subject' | 'expiresAt'>

export type AuthReaction =
  | { readonly _tag: 'logout' }
  | { readonly _tag: 'renew' }
  | { readonly _tag: 'none' }

const signedOut: AuthPair = { subject: null, expiresAt: null }

// Each renewal is a hidden-iframe call to Auth0, and a same-origin script can request any number.
export const DEFAULT_RENEW_MIN_INTERVAL_MS = 10_000

// `allowInteractive: false` because no shopper asked for this renewal, so a popup is never right.
// `updateTokenContext: true` because each reaction here (a sign-in, an account switch, or a fresher
// token) needs the new token in the context Apollo sends.
const renewOptions: AccessTokenFactoryOptions = {
  ignoreCache: true,
  updateTokenContext: true,
  allowInteractive: false,
}

const isAuthMessage = (message: CrossTabMessage): message is AuthMessage => message._tag === 'auth'

const samePair = (previous: AuthPair, current: AuthPair): boolean =>
  previous.subject === current.subject && previous.expiresAt === current.expiresAt

/**
 * This tab's settled `(subject, expiresAt)`. A signed-in state whose token has not arrived yet is
 * dropped, so peers never hear a subject without the expiry the renew rule compares against.
 */
export const deriveOwnAuthPair = (
  authState$: Observable<AuthState>,
  accessToken$: Observable<string | undefined>,
): Observable<AuthPair> =>
  combineLatest([authState$.pipe(filter(({ isLoading }) => !isLoading)), accessToken$]).pipe(
    map(([{ isAuthenticated, user }, token]): AuthPair => {
      const subject = isAuthenticated ? authSubject(user) : null
      // A signed-out tab can still hold the old token for a render; it says nothing to peers.
      return subject === null
        ? signedOut
        : { subject, expiresAt: token ? accessTokenExpiresAt(token) : null }
    }),
    filter(({ subject, expiresAt }) => subject === null || expiresAt !== null),
    distinctUntilChanged(samePair),
  )

export const authReaction = (
  message: AuthPair,
  own: AuthPair & { readonly now: number },
): AuthReaction => {
  if (message.subject === null) {
    return own.subject === null ? { _tag: 'none' } : { _tag: 'logout' }
  }
  if (own.subject !== message.subject) {
    return { _tag: 'renew' }
  }
  // Only inside this tab's own expiring-soon window, so a peer's page load costs nothing, and a
  // renewal here cannot bounce back because the renewed tab is no longer expiring soon.
  const peerIsFresher =
    own.expiresAt !== null &&
    own.expiresAt - own.now <= expiringSoonThreshold &&
    message.expiresAt !== null &&
    message.expiresAt > own.expiresAt
  return peerIsFresher ? { _tag: 'renew' } : { _tag: 'none' }
}

const recordRenewFailure = (error: unknown): void => {
  // `login_required` is expected when the Auth0 session is gone or silent auth is blocked, so this
  // is context for a later event, not an event of its own.
  const code: unknown =
    typeof error === 'object' && error !== null ? Reflect.get(error, 'error') : undefined
  logAndAddBreadcrumb({
    category: 'auth',
    level: 'debug',
    message: 'Cross-tab token renewal failed',
    data: { code: typeof code === 'string' ? code : undefined },
  })
}

export type AccessTokenFactory = (options?: AccessTokenFactoryOptions) => Observable<string>

export interface RenewCap {
  readonly now: () => number
  readonly minIntervalMs: number
}

/**
 * At most one renewal per `minIntervalMs`, with a trailing one: a request inside the interval waits
 * for the interval to end instead of being dropped, so a change heard inside it (a sign-in followed
 * by an account switch) is still applied, while a flood costs one renewal per interval.
 */
export const createCappedRenew = (
  renew$: Observable<unknown>,
  { now, minIntervalMs }: RenewCap,
): Observable<unknown> => {
  const lastRenewAt$ = new BehaviorSubject<number | null>(null)
  const renewNow$ = defer(() => {
    lastRenewAt$.next(now())
    return renew$
  })
  return lastRenewAt$.pipe(
    take(1),
    concatMap((last) => {
      const at = now()
      // A clock stepped backwards would otherwise hold every renewal until it caught up.
      const waitMs = last === null || at < last ? 0 : Math.max(0, last + minIntervalMs - at)
      return waitMs === 0 ? renewNow$ : timer(waitMs).pipe(concatMap(() => renewNow$))
    }),
  )
}

export interface RenewDeps {
  readonly accessTokenFactory$: Observable<AccessTokenFactory>
  readonly onRenewError?: (error: unknown) => void
}

/** One renewal with the given options; a failure is recorded and completes empty. */
export const createRenew = (
  { accessTokenFactory$, onRenewError = recordRenewFailure }: RenewDeps,
  options: AccessTokenFactoryOptions,
): Observable<string> =>
  accessTokenFactory$.pipe(
    take(1),
    switchMap((getAccessToken) => getAccessToken(options)),
    catchError((error: unknown) => {
      onRenewError(error)
      return EMPTY
    }),
  )

export interface CrossTabAuthDeps extends RenewDeps {
  readonly channel: CrossTabChannel
  readonly ownAuthPair$: Observable<AuthPair>
  readonly localLogout$: Observable<() => void>
  readonly signalRemoteSignOut: () => void
  readonly visibility?: VisibilityGate
  readonly now?: () => number
  readonly renewMinIntervalMs?: number
}

export const createCrossTabAuth = ({
  channel,
  ownAuthPair$,
  accessTokenFactory$,
  localLogout$,
  signalRemoteSignOut,
  visibility,
  now = Date.now,
  onRenewError,
  renewMinIntervalMs = DEFAULT_RENEW_MIN_INTERVAL_MS,
}: CrossTabAuthDeps): Observable<unknown> => {
  const sharedOwnAuthPair$ = ownAuthPair$.pipe(shareReplay({ bufferSize: 1, refCount: true }))

  // The replay answers synchronously once a pair has settled; until then the tab counts as signed
  // out.
  const currentOwnAuthPair$ = merge(sharedOwnAuthPair$, of(signedOut)).pipe(take(1))

  // Only signed-in pairs: a sign-out is announced by `announceSignOutThenLogout`, and a signed-out
  // pair here is a local session expiry, which peers must not follow into a logout.
  const publisher$ = sharedOwnAuthPair$.pipe(
    filter(({ subject }) => subject !== null),
    tap(({ subject, expiresAt }) => channel.publish({ _tag: 'auth', subject, expiresAt })),
  )

  const cappedRenew$ = createCappedRenew(
    createRenew({ accessTokenFactory$, onRenewError }, renewOptions),
    { now, minIntervalMs: renewMinIntervalMs },
  )

  // The signal lets the cart reset before the auth state flips; a local session expiry flips the
  // same state and must leave the cart alone.
  const logout$ = localLogout$.pipe(
    take(1),
    tap((logout) => {
      signalRemoteSignOut()
      logout()
    }),
  )

  const consumer$ = channel.incoming$.pipe(
    // Per source: a peer that logs out on a sign-out no longer re-announces it, so a hidden tab must
    // keep the sign-out it heard even when another tab publishes a signed-in pair after it.
    latestVisibleByTag({ ...visibility, groupKey: byTagAndSource }),
    map(({ message }) => message),
    filter(isAuthMessage),
    // Messages are state announcements, so each is queued in order and judged against this tab's
    // pair when its turn comes: a sign-out heard during a renewal still logs out afterwards.
    concatMap((message) =>
      currentOwnAuthPair$.pipe(
        map((own) => authReaction(message, { ...own, now: now() })),
        concatMap((reaction): Observable<unknown> => {
          switch (reaction._tag) {
            case 'renew':
              return cappedRenew$
            case 'logout':
              return logout$
            case 'none':
              return EMPTY
          }
        }),
      ),
    ),
  )

  return merge(publisher$, consumer$)
}

export const crossTabAuth$: Observable<unknown> = createCrossTabAuth({
  channel: crossTabChannel,
  ownAuthPair$: deriveOwnAuthPair(latestAuthState$, latestAccessToken$),
  accessTokenFactory$: latestAccessTokenFactory$,
  localLogout$: latestLocalLogout$,
  signalRemoteSignOut,
})
